/** Radix tab panels, native hidden, and assistive visibility. */
const PANEL_VISIBILITY_ATTRIBUTE_NAMES = [
  'data-state',
  'hidden',
  'aria-hidden',
] as const;

/** Inline / class-driven visibility (frequent in React; opt-in for mutation watching). */
const EXTENDED_VISIBILITY_ATTRIBUTE_NAMES = ['style', 'class'] as const;

/** Default MutationObserver `attributeFilter` for view tracking. */
export const VIEW_VISIBILITY_MUTATION_ATTRIBUTE_FILTER: string[] = [
  ...PANEL_VISIBILITY_ATTRIBUTE_NAMES,
];

export const VIEW_VISIBILITY_EXTENDED_MUTATION_ATTRIBUTE_FILTER: string[] = [
  ...PANEL_VISIBILITY_ATTRIBUTE_NAMES,
  ...EXTENDED_VISIBILITY_ATTRIBUTE_NAMES,
];

export function resolveViewVisibilityMutationAttributeFilter(options?: {
  readonly includeClassAndStyleMutations?: boolean;
  readonly attributeFilter?: readonly string[];
}): string[] {
  if (options?.attributeFilter) {
    return [...options.attributeFilter];
  }
  if (options?.includeClassAndStyleMutations) {
    return [...VIEW_VISIBILITY_EXTENDED_MUTATION_ATTRIBUTE_FILTER];
  }
  return [...VIEW_VISIBILITY_MUTATION_ATTRIBUTE_FILTER];
}

function hasZeroOpacity(style: CSSStyleDeclaration): boolean {
  const opacity = Number.parseFloat(style.opacity);
  return Number.isFinite(opacity) && opacity === 0;
}

function hasSemanticVisibilityBlock(element: Element): boolean {
  let current: Element | null = element;
  while (current) {
    if (current.hasAttribute('hidden')) {
      return true;
    }
    if (current.getAttribute('aria-hidden') === 'true') {
      return true;
    }
    if (current.getAttribute('data-state') === 'inactive') {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}

function isHiddenByAncestorCss(element: Element): boolean {
  let current: Element | null = element.parentElement;
  while (current) {
    if (typeof window !== 'undefined') {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return true;
      }
      if (hasZeroOpacity(style)) {
        return true;
      }
    }

    current = current.parentElement;
  }

  return false;
}

function isCssVisibleForViewTracking(element: Element): boolean {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({
      checkOpacity: true,
      checkVisibilityCSS: true,
    });
  }

  if (typeof window !== 'undefined') {
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    if (hasZeroOpacity(style)) {
      return false;
    }
  }

  return !isHiddenByAncestorCss(element);
}

/** Whether an element should count as user-visible for passive view tracking. */
export function isElementVisibleForViewTracking(element: Element): boolean {
  if (hasSemanticVisibilityBlock(element)) {
    return false;
  }

  return isCssVisibleForViewTracking(element);
}

function resolveThresholdRatio(
  threshold: number | number[] | undefined,
): number {
  if (threshold === undefined) {
    return 0.5;
  }
  if (Array.isArray(threshold)) {
    return threshold.length > 0 ? Math.max(...threshold) : 0.5;
  }
  return threshold;
}

/** Viewport intersection ratio for re-checks when visibility changes without a new IO callback. */
export function computeViewportIntersectionRatio(element: Element): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const intersectLeft = Math.max(0, rect.left);
  const intersectTop = Math.max(0, rect.top);
  const intersectRight = Math.min(viewportWidth, rect.right);
  const intersectBottom = Math.min(viewportHeight, rect.bottom);

  if (intersectRight <= intersectLeft || intersectBottom <= intersectTop) {
    return 0;
  }

  const intersectArea =
    (intersectRight - intersectLeft) * (intersectBottom - intersectTop);
  const elementArea = rect.width * rect.height;
  return intersectArea / elementArea;
}

export function meetsViewIntersectionThreshold(
  element: Element,
  threshold: number | number[] | undefined,
): boolean {
  return (
    computeViewportIntersectionRatio(element) >=
    resolveThresholdRatio(threshold)
  );
}
